/** Single-line filter bar that collapses least-important filters into a
 *  "More filters" popover when the viewport is too narrow (the project-wide
 *  grid toolbar rule — see aidocs/context/components.md).
 *
 *  Widths are measured from an offscreen ghost row holding every item (so a
 *  hidden item's width is still known when the bar grows again). A
 *  ResizeObserver on the visible row recomputes, on each resize, the largest
 *  set of items that fits — preferring higher `priority` — and drops the rest
 *  into the popover. The row never wraps.
 */
import FilterListIcon from '@mui/icons-material/FilterList';
import { Badge, Box, Button, Popover, Stack } from '@mui/material';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface FilterBarItem {
  key: string;
  node: ReactNode;
  /** Higher stays inline longer; lower collapses into "More filters" first.
   *  Default 0. */
  priority?: number;
  /** True when this filter currently holds a value (drives the More badge). */
  active?: boolean;
}

const GAP_PX = 12;
const MORE_BTN_PX = 130; // approx width of the "More filters" button

export function ResponsiveFilterBar({ items }: { items: FilterBarItem[] }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(items.map((i) => i.key)),
  );
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const itemsKey = items.map((i) => i.key).join('|');

  useLayoutEffect(() => {
    const row = rowRef.current;
    const ghost = ghostRef.current;
    if (!row || !ghost) return;

    const measure = () => {
      const widths = new Map<string, number>();
      ghost.querySelectorAll<HTMLElement>('[data-fbk]').forEach((el) => {
        widths.set(el.dataset.fbk!, el.offsetWidth);
      });
      const avail = row.clientWidth;

      const total = items.reduce((s, it) => s + (widths.get(it.key) ?? 0) + GAP_PX, 0) - GAP_PX;
      let shown: Set<string>;
      if (total <= avail) {
        shown = new Set(items.map((i) => i.key));
      } else {
        // Reserve room for the More button, then greedily admit by priority.
        const budget = avail - MORE_BTN_PX;
        const byPriority = [...items].sort(
          (a, b) => (b.priority ?? 0) - (a.priority ?? 0),
        );
        shown = new Set<string>();
        let used = 0;
        for (const it of byPriority) {
          const w = (widths.get(it.key) ?? 0) + GAP_PX;
          if (used + w <= budget) {
            shown.add(it.key);
            used += w;
          }
        }
      }
      setVisibleKeys((prev) => {
        if (prev.size === shown.size && [...shown].every((k) => prev.has(k))) return prev;
        return shown;
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  const hidden = items.filter((i) => !visibleKeys.has(i.key));
  const hiddenActive = hidden.filter((i) => i.active).length;

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      {/* Offscreen measurement copy — every item, never collapsed. */}
      <Box
        ref={ghostRef}
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          gap: `${GAP_PX}px`,
          top: 0,
          left: 0,
          zIndex: -1,
        }}
      >
        {items.map((it) => (
          <div key={it.key} data-fbk={it.key}>
            {it.node}
          </div>
        ))}
      </Box>

      {/* Visible single-line row. Clip horizontally only (keeps the one-line
          layout) — `overflow: hidden` would also clip the fields' floating
          labels at the top. `clip` is not a scroll value, so overflow-y stays
          visible and the labels render fully. */}
      <Box
        ref={rowRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${GAP_PX}px`,
          flexWrap: 'nowrap',
          overflowX: 'clip',
          overflowY: 'visible',
        }}
      >
        {items.filter((i) => visibleKeys.has(i.key)).map((it) => (
          <Box key={it.key} sx={{ flexShrink: 0 }}>
            {it.node}
          </Box>
        ))}
        {hidden.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={
              <Badge color="primary" badgeContent={hiddenActive} variant="dot" invisible={!hiddenActive}>
                <FilterListIcon fontSize="small" />
              </Badge>
            }
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            More filters
          </Button>
        )}
      </Box>

      <Popover
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={1.5} sx={{ p: 2, minWidth: 260 }}>
          {hidden.map((it) => (
            <Box key={it.key}>{it.node}</Box>
          ))}
        </Stack>
      </Popover>
    </Box>
  );
}
